import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { fetchExecutiveSummary, generateAIReport } from '../api';
import { presetRange, PRESET_KEYS, PRESET_LABEL_KEY } from '../utils/dateRange';
// PDF is now generated server-side via Puppeteer
import { useLanguage } from '../LanguageContext';
import { useBrand } from '../BrandContext';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EmptyState from '../components/EmptyState';

const COLORS = { snapchat: '#FFFC00', tiktok: '#EE1D52', clarity: '#0078D4', google: '#4285F4', instagram: '#E1306C', x: '#1DA1F2' };

function getLocalDate(daysAgo = 0) {
  // Use KSA timezone to get the correct date regardless of browser timezone
  const d = new Date(); d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' }); // en-CA gives YYYY-MM-DD format
}

const fmtK = n => { if (n == null || isNaN(n)) return '—'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(1)+'K'; return Number(n).toLocaleString(); };
const fmtSAR = n => n != null && !isNaN(n) ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SAR' : '—';
const fmtPct = n => n != null && !isNaN(n) ? Number(n).toFixed(2) + '%' : '—';
const fmtTime = s => { if (!s) return '—'; const sec = Number(s); if (sec < 60) return sec.toFixed(0) + 's'; return Math.floor(sec/60) + 'm ' + Math.round(sec%60) + 's'; };

function KPI({ label, value, color, icon, small }) {
  return (
    <div className="kpi-card">
      <div className="kpi-header"><span className="kpi-title">{label}</span>{icon && <span className="kpi-icon" aria-hidden="true">{icon}</span>}</div>
      <div className="kpi-value" style={{ color, fontSize: small ? '1.4rem' : undefined }}>{value}</div>
    </div>
  );
}

export default function Executive() {
  const { t, isRTL } = useLanguage();
  const brand = useBrand();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('yesterday');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const getDates = useCallback(() => presetRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const loadData = useCallback(async () => {
    const { from, to } = getDates();
    // A custom range needs both ends before it can fetch — otherwise the empty
    // dates would silently fall back to the server's default (yesterday).
    if (preset === 'custom' && (!from || !to)) { setLoading(false); return; }
    setLoading(true);
    const result = await fetchExecutiveSummary(from, to);
    if (!result.error) setData(result);
    setLoading(false);
  }, [getDates, preset]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAIReport = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const { from, to } = getDates();

      // Step 1: Get AI analysis
      let analysis = null;
      try {
        const result = await generateAIReport(from, to);
        if (!result.error) analysis = result.analysis;
      } catch { /* AI analysis is optional — PDF still generates with raw data */ }

      // Step 2: Generate PDF server-side (Puppeteer — proper Arabic/RTL)
      const token = localStorage.getItem('vroia-token');
      const pdfRes = await fetch('/api/report/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ date_from: from, date_to: to, analysis }),
      });
      if (!pdfRes.ok) { const err = await pdfRes.json().catch(() => ({})); throw new Error(err.error || 'PDF generation failed'); }

      const blob = await pdfRes.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${brand.brandName}_Report_${from}_${to}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('AI report error:', err);
      setAiError(err.message || 'Failed to generate report');
    }
    setAiLoading(false);
  };

  const tooltipStyle = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: '0.8rem', boxShadow: 'var(--card-hover-shadow)' };

  const presets = PRESET_KEYS.map(key => ({ key, label: t(PRESET_LABEL_KEY[key]) }));

  const tot = data?.totals || {};
  const plat = data?.platforms || {};
  const leads = data?.leadsSummary || {};

  const trendData = (data?.dailyTrend || []).map(d => ({
    ...d, date: d.date?.slice(5),
    snapchat: Math.round((d.snapchat||0)*100)/100,
    tiktok: Math.round((d.tiktok||0)*100)/100,
    google: Math.round((d.google||0)*100)/100,
  }));

  const pieData = (data?.spendBreakdown || []).map(d => ({
    ...d, spend: Math.round(d.spend*100)/100,
    color: d.platform === 'Snapchat' ? COLORS.snapchat : d.platform === 'TikTok' ? COLORS.tiktok : COLORS.google,
  }));

  const channelBars = [];
  if (plat.snapchat?.connected && plat.snapchat.spend > 0) channelBars.push({ name: 'Snapchat', ...plat.snapchat, color: COLORS.snapchat });
  if (plat.tiktok?.connected && plat.tiktok.spend > 0) channelBars.push({ name: 'TikTok', ...plat.tiktok, color: COLORS.tiktok });
  if (plat.google?.connected && plat.google.spend > 0) channelBars.push({ name: 'Google Ads', ...plat.google, color: COLORS.google });

  const Skel = () => <div className="kpi-card"><LoadingSkeleton width="60%" height="14px" /><LoadingSkeleton width="80%" height="28px" style={{ marginTop: 8 }} /></div>;

  return (
    <div>
      {/* ── Filter Bar ── */}
      <div className="filter-bar">
        <div className="tabs">
          {presets.map(p => (
            <button key={p.key} className={`tab ${preset === p.key ? 'active' : ''}`} onClick={() => setPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginInlineStart: 8 }}>
            <input type="date" value={customFrom} max={customTo || getLocalDate(0)} onChange={e => setCustomFrom(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.78rem' }} />
            <span className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('to')}</span>
            <input type="date" value={customTo} min={customFrom || undefined} max={getLocalDate(0)} onChange={e => setCustomTo(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.78rem' }} />
          </div>
        )}
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="text-secondary" style={{ fontSize: '0.75rem' }}>
            {tot.platforms_connected || 0}/{tot.platforms_total || 6} {t('execPlatforms')?.toLowerCase()}
          </span>
          <button className="btn btn-sm" onClick={handleAIReport} disabled={aiLoading || loading}
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, fontWeight: 600, fontSize: '0.78rem', cursor: aiLoading ? 'wait' : 'pointer', opacity: aiLoading ? 0.7 : 1 }}>
            {aiLoading ? (
              <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> {t('aiGenerating') || 'Generating...'}</>
            ) : (
              <><span style={{ fontSize: '1rem' }}>&#9733;</span> {t('aiReportBtn') || 'AI Report'}</>
            )}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}>
            {loading ? '...' : t('refresh')}
          </button>
        </div>
      </div>

      {/* AI Error Toast */}
      {aiError && (
        <div style={{ background: 'var(--error)', color: '#fff', padding: '10px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
          <span>{aiError}</span>
          <button onClick={() => setAiError(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.1rem' }}>&times;</button>
        </div>
      )}

      {/* Spinner keyframe (inline) */}
      {aiLoading && <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>}

      {/* When the selected period has no ad delivery, the KPIs are 0 by design (the
          numbers honor the filter now). Say WHY + when data last existed, so 0 doesn't
          read as broken. */}
      {!loading && Number(tot.spend || 0) === 0 && Number(tot.impressions || 0) === 0 && (() => {
        const last = [plat.snapchat?.latestDataDate, plat.tiktok?.latestDataDate].filter(Boolean).sort().pop();
        return (
          <div style={{ background: '#f59e0b14', border: '1px solid #f59e0b55', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.82rem', color: '#b45309', lineHeight: 1.6 }}>
            ⚠️ {isRTL
              ? `لا يوجد إنفاق إعلاني في الفترة المحددة${last ? ` — آخر بيانات متوفرة بتاريخ ${last} (تأكد أن الحملات ليست متوقفة)` : ''}.`
              : `No ad delivery in the selected period${last ? ` — last data available ${last} (check the campaigns aren't paused)` : ''}.`}
          </div>
        );
      })()}

      {/* ── Ad Spend KPIs ── */}
      <div className="grid grid-4 mb-4">
        {loading ? [1,2,3,4].map(i => <Skel key={i} />) : <>
          <KPI label={t('execTotalSpend')} value={fmtSAR(tot.spend)} color="var(--accent)" icon="&#65020;" />
          <KPI label={t('execTotalImpressions')} value={fmtK(tot.impressions)} icon="&#9678;" />
          <KPI label={t('execTotalClicks')} value={fmtK(tot.clicks)} icon="&#8599;" />
          <div className="kpi-card" title={t('execLeadsReceivedDesc') || 'Total leads received in CRM from all sources'}>
            <div className="kpi-header"><span className="kpi-title">{t('execLeadsReceived') || 'Leads Received'}</span><span className="kpi-icon" aria-hidden="true">&#128100;</span></div>
            <div className="kpi-value" style={{ color: 'var(--success)' }}>{fmtK(tot.leadsReceived)}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{tot.costPerLead > 0 ? `${t('execCostPerLead') || 'Cost/Lead'}: ${fmtSAR(tot.costPerLead)}` : ''}</div>
          </div>
        </>}
      </div>

      <div className="grid grid-3 mb-6">
        {loading ? [1,2,3].map(i => <Skel key={i} />) : <>
          <div className="kpi-card" title={t('hintCTR')}>
            <div className="kpi-header"><span className="kpi-title">{t('execCTRLabel')}</span></div>
            <div className="kpi-value" style={{ color: 'var(--success)', fontSize: '1.4rem' }}>{fmtPct(tot.ctr)}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('execCTRDesc')}</div>
          </div>
          <div className="kpi-card" title={t('hintCPC')}>
            <div className="kpi-header"><span className="kpi-title">{t('execCPCLabel')}</span></div>
            <div className="kpi-value" style={{ fontSize: '1.4rem' }}>{fmtSAR(tot.cpc)}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('execCPCDesc')}</div>
          </div>
          <div className="kpi-card" title={t('hintCPM')}>
            <div className="kpi-header"><span className="kpi-title">{t('execCPMLabel')}</span></div>
            <div className="kpi-value" style={{ fontSize: '1.4rem' }}>{fmtSAR(tot.cpm)}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('execCPMDesc')}</div>
          </div>
        </>}
      </div>

      {/* ── CRM Lead Summary (inline bar) ── */}
      {leads.total > 0 && (
        <div className="card mb-6" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={() => navigate('/leads')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('crmTotalLeads')}</span>
            <span className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('clicks')} →</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {[
              { label: t('crmTotalLeads'), value: leads.total, color: 'var(--text-primary)' },
              { label: t('execNewLast24h') || 'New (24h)', value: leads.newLast24h, color: leads.newLast24h > 0 ? 'var(--success)' : 'var(--text-secondary)' },
              { label: t('crmUnassigned') || 'Unassigned', value: leads.unassigned, color: leads.unassigned > 0 ? 'var(--error)' : 'var(--success)' },
              { label: t('crmPotential') || 'Potential', value: leads.potential, color: '#8b5cf6' },
              { label: t('crmPotentialRate') || 'Rate', value: leads.potentialRate + '%', color: 'var(--success)' },
            ].map((item, i) => (
              <div key={i} style={{ flex: 1, minWidth: 100, textAlign: 'center', padding: '14px 8px', borderRight: i < 4 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: item.color }}>{item.value}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.3 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Platform Cards ── */}
      <h2 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 14, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('execPlatformBreakdown')}</h2>
      <div className="grid grid-3 mb-6">
        {[
          { key: 'snapchat', name: 'Snapchat', color: COLORS.snapchat, icon: '👻', link: '/channels?platform=snapchat' },
          { key: 'tiktok', name: 'TikTok', color: COLORS.tiktok, icon: '🎵', link: '/channels?platform=tiktok' },
          { key: 'google', name: 'Google Ads', color: COLORS.google, icon: '🔍', link: '/channels?platform=google' },
          ...(brand.clarityEnabled ? [{ key: 'clarity', name: 'Clarity', color: COLORS.clarity, icon: '📊', link: '/yamam' }] : []),
          { key: 'instagram', name: 'Instagram', color: COLORS.instagram, icon: '📸' },
          { key: 'x', name: 'X', color: COLORS.x, icon: '𝕏' },
        ].map(p => {
          const pd = plat[p.key];
          const on = pd?.connected;
          const isAd = ['snapchat','tiktok','google'].includes(p.key);
          return (
            <div key={p.key} className="card" style={{ borderTop: `3px solid ${on ? p.color : 'var(--border)'}`, opacity: on ? 1 : 0.6, cursor: on && p.link ? 'pointer' : 'default', padding: '16px 18px' }}
              onClick={() => on && p.link && navigate(p.link)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: on ? 12 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1.2rem' }}>{p.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</span>
                </div>
                {on ? <span className="badge badge-active" style={{ fontSize: '0.6rem', padding: '2px 8px' }}>Live</span>
                  : <span className="badge" style={{ background: 'rgba(139,143,163,0.1)', color: 'var(--text-secondary)', fontSize: '0.6rem', padding: '2px 8px' }}>{t('execComingSoon')}</span>}
              </div>
              {on && isAd && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('spend')}</div><div style={{ fontWeight: 700, color: p.color, fontSize: '0.95rem' }}>{fmtSAR(pd.spend)}</div></div>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('impressions')}</div><div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{fmtK(pd.impressions)}</div></div>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('clicks')}</div><div style={{ fontWeight: 600 }}>{fmtK(pd.clicks)}</div></div>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('ctr')}</div><div style={{ fontWeight: 600, color: 'var(--success)' }}>{fmtPct(pd.ctr)}</div></div>
                </div>
              )}
              {on && isAd && Number(pd.spend || 0) === 0 && Number(pd.impressions || 0) === 0 && pd.latestDataDate && (
                <div className="text-secondary" style={{ fontSize: '0.62rem', marginTop: 8 }}>📅 {isRTL ? `لا إنفاق بالفترة · آخر بيانات ${pd.latestDataDate}` : `no delivery · last data ${pd.latestDataDate}`}</div>
              )}
              {on && p.key === 'clarity' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('execUsers')}</div><div style={{ fontWeight: 700, color: p.color }}>{fmtK(pd.users)}</div></div>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('execSessions')}</div><div style={{ fontWeight: 600 }}>{fmtK(pd.sessions)}</div></div>
                  <div><div className="text-secondary" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{t('execAvgTime')}</div><div style={{ fontWeight: 600 }}>{fmtTime(pd.avgActiveTime)}</div></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-2 mb-6">
        <div className="card">
          <div className="card-header"><h2>{t('execSpendTrend')}</h2></div>
          {loading ? <LoadingSkeleton width="100%" height="280px" /> : trendData.length > 0 ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.snapchat} stopOpacity={0.3}/><stop offset="95%" stopColor={COLORS.snapchat} stopOpacity={0}/></linearGradient>
                    <linearGradient id="gt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.tiktok} stopOpacity={0.3}/><stop offset="95%" stopColor={COLORS.tiktok} stopOpacity={0}/></linearGradient>
                    <linearGradient id="gg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.google} stopOpacity={0.3}/><stop offset="95%" stopColor={COLORS.google} stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" stroke="var(--text-secondary)" tick={{ fill:'var(--text-secondary)', fontSize:11 }} />
                  <YAxis stroke="var(--text-secondary)" tick={{ fill:'var(--text-secondary)', fontSize:11 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Area type="monotone" dataKey="snapchat" stroke={COLORS.snapchat} fill="url(#gs)" strokeWidth={2} name="Snapchat" />
                  <Area type="monotone" dataKey="tiktok" stroke={COLORS.tiktok} fill="url(#gt)" strokeWidth={2} name="TikTok" />
                  <Area type="monotone" dataKey="google" stroke={COLORS.google} fill="url(#gg)" strokeWidth={2} name="Google" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState icon="&#128200;" title={t('execNoData')} />}
        </div>

        <div className="card">
          <div className="card-header"><h2>{t('execSpendDistribution')}</h2></div>
          {loading ? <LoadingSkeleton width="100%" height="280px" /> : pieData.length > 0 ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} innerRadius={50}
                    dataKey="spend" nameKey="platform" paddingAngle={4}
                    label={({platform,percent})=>`${platform} ${(percent*100).toFixed(0)}%`}
                    labelLine={{ stroke:'var(--text-secondary)', strokeWidth:1 }}>
                    {pieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={v => fmtSAR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState icon="&#128176;" title={t('execNoData')} />}
        </div>
      </div>

      {/* ── Channel Comparison Table ── */}
      {channelBars.length > 0 && (
        <div className="card">
          <div className="card-header"><h2>{t('execChannelPerformance')}</h2></div>
          <div className="data-table-wrapper">
            <table className="data-table" style={{ fontSize: '0.82rem' }}>
              <thead><tr>
                <th>{t('platform')}</th><th>{t('spend')}</th><th>{t('impressions')}</th><th>{t('clicks')}</th><th>{t('ctr')}</th><th>{t('cpc')}</th><th>{t('cpm')}</th>
              </tr></thead>
              <tbody>
                {channelBars.map((ch,i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>
                      <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:ch.color, marginRight:8, verticalAlign:'middle' }}/>{ch.name}
                    </td>
                    <td style={{ fontWeight:700, color:'var(--accent)' }}>{fmtSAR(ch.spend)}</td>
                    <td>{fmtK(ch.impressions)}</td><td>{fmtK(ch.clicks)}</td>
                    <td style={{ color:'var(--success)' }}>{ch.impressions > 0 ? fmtPct((ch.clicks/ch.impressions)*100) : '—'}</td>
                    <td>{ch.clicks > 0 ? fmtSAR(ch.spend/ch.clicks) : '—'}</td>
                    <td>{ch.impressions > 0 ? fmtSAR((ch.spend/ch.impressions)*1000) : '—'}</td>
                  </tr>
                ))}
                {channelBars.length > 1 && (
                  <tr style={{ fontWeight:700, borderTop:'2px solid var(--border)' }}>
                    <td>{t('all')}</td>
                    <td style={{ color:'var(--accent)' }}>{fmtSAR(tot.spend)}</td>
                    <td>{fmtK(tot.impressions)}</td><td>{fmtK(tot.clicks)}</td>
                    <td style={{ color:'var(--success)' }}>{fmtPct(tot.ctr)}</td>
                    <td>{fmtSAR(tot.cpc)}</td><td>{fmtSAR(tot.cpm)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
